/**
 * Product Tour Configuration
 * Defines the steps for the user onboarding tour using driver.js
 */

import type { DriveStep } from 'driver.js';

export const TOUR_STORAGE_KEY = 'kt_tour_seen_v1';

export const tourSteps: DriveStep[] = [
  // Step 1: Welcome
  {
    popover: {
      title: 'ಕನ್ನಡ ತಿಪಿಟಕಕ್ಕೆ ಸ್ವಾಗತ! 🙏',
      description: `
        <p>ಬುದ್ಧನ ಮೂಲ ಬೋಧನೆಗಳನ್ನು ಕನ್ನಡದಲ್ಲಿ ಓದಿ, ಅರ್ಥಮಾಡಿಕೊಳ್ಳಿ.</p>
        <p class="text-sm text-gray-500 mt-2">ಈ ಸಂಕ್ಷಿಪ್ತ ಪರಿಚಯವನ್ನು ನೋಡೋಣ.</p>
      `,
      side: 'over',
      align: 'center',
    },
  },

  // Step 2: Search
  {
    element: '#tour-search-container',
    popover: {
      title: 'ಹುಡುಕಾಟ 🔍',
      description: `
        <p>ಯಾವುದೇ ಸೂತ್ರವನ್ನು ಹೆಸರು ಅಥವಾ ಸಂಖ್ಯೆಯ ಮೂಲಕ ಹುಡುಕಿ.</p>
        <p class="text-sm text-gray-500 mt-2">ಉದಾ: "ಧಮ್ಮಚಕ್ಕ" ಅಥವಾ "sn35.1"</p>
      `,
      side: 'bottom',
      align: 'center',
    },
  },

  // Step 3: View Mode Tabs (NEW)
  {
    element: '[data-tour="view-tabs"]',
    popover: {
      title: 'ವೀಕ್ಷಣಾ ವಿಧಾನ 📑',
      description: `
        <p>ಓದುವ ವಿಧಾನವನ್ನು ಆಯ್ಕೆಮಾಡಿ:</p>
        <ul class="text-sm mt-2 space-y-1">
          <li>• <b>ಪಾಳಿ</b> - ಮೂಲ ಪಾಳಿ ಮಾತ್ರ</li>
          <li>• <b>ಕನ್ನಡ</b> - ಅನುವಾದ ಮಾತ್ರ</li>
          <li>• <b>ಪಾಳಿ-ಕನ್ನಡ</b> - ಎರಡೂ ಪಕ್ಕದಲ್ಲಿ</li>
          <li>• <b>ಸಾರಾಂಶ</b> / <b>ಟಿಪ್ಪಣಿ</b></li>
        </ul>
      `,
      side: 'bottom',
      align: 'center',
    },
  },

  // Step 4: Reading Area
  {
    element: '[data-tour="sutta-content"]',
    popover: {
      title: 'ಓದುವ ಸ್ಥಳ 📖',
      description: `
        <p>ಇಲ್ಲಿ ಪಾಳಿ ಮತ್ತು ಕನ್ನಡ ಅನುವಾದವನ್ನು ಓದಿ.</p>
        <p class="text-sm text-gray-500 mt-2">ಪದಗಳ ಮೇಲೆ ಒತ್ತಿದರೆ ಅರ್ಥ ಕಾಣಿಸುತ್ತದೆ.</p>
      `,
      side: 'top',
      align: 'center',
    },
  },

  // Step 5: Action Buttons (NEW)
  {
    element: '[data-tour="action-buttons"]',
    popover: {
      title: 'ಓದುವ ಟ್ರ್ಯಾಕಿಂಗ್ ✅',
      description: `
        <p>ನಿಮ್ಮ ಓದುವಿಕೆಯನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ:</p>
        <ul class="text-sm mt-2 space-y-1">
          <li>• <b>✓</b> - ಓದಿದ ಎಂದು ಗುರುತಿಸಿ</li>
          <li>• <b>🔖</b> - ಬುಕ್‌ಮಾರ್ಕ್ ಸೇರಿಸಿ</li>
        </ul>
      `,
      side: 'bottom',
      align: 'end',
    },
  },

  // Step 6: Translator Selector (conditional)
  {
    element: '#tour-translator-select',
    popover: {
      title: 'ಅನುವಾದ ಆಯ್ಕೆ 🔄',
      description: `
        <p>ವಿವಿಧ ಅನುವಾದಕರ ಆವೃತ್ತಿಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡಿ.</p>
      `,
      side: 'bottom',
      align: 'start',
    },
  },

  // Step 7: Navigation (NEW)
  {
    element: '[data-tour="sutta-navigation"]',
    popover: {
      title: 'ಮುಂದಿನ/ಹಿಂದಿನ ಸೂತ್ರ ⬅️➡️',
      description: `
        <p>ಸೂತ್ರಗಳ ನಡುವೆ ನ್ಯಾವಿಗೇಟ್ ಮಾಡಿ.</p>
        <p class="text-sm text-gray-500 mt-2">💡 ಸಲಹೆ: ← → ಕೀಬೋರ್ಡ್ ಬಳಸಿ!</p>
      `,
      side: 'top',
      align: 'center',
    },
  },

  // Step 8: Settings
  {
    element: '#tour-settings-btn',
    popover: {
      title: 'ಸಂಯೋಜನೆಗಳು ⚙️',
      description: `
        <p>ಥೀಮ್ (ಬೆಳಕು/ಕತ್ತಲೆ/ಸೀಪಿಯಾ) ಮತ್ತು ಅಕ್ಷರ ಗಾತ್ರವನ್ನು ಬದಲಾಯಿಸಿ.</p>
      `,
      side: 'left',
      align: 'center',
    },
  },
];

export default tourSteps;
